import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Row,
  Col,
  Label,
  Input,
  Button,
  FormFeedback,
  InputGroup,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import { addNewUser } from "../../../slices/user/thunk";
import { resetUserFlags } from "../../../slices/user/reducer";

const selectUserState = createSelector(
  (state) => state.User,
  (user) => ({
    users: user.users || [],
    isLoading: user.isLoading || false,
    isUserCreated: user.isUserCreated || false,
    error: user.error,
  })
);

const UserCreationModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const { users, isLoading, isUserCreated, error } = useSelector(selectUserState);

  const [formData, setFormData] = useState({
    username: "",
    password1: "",
    password2: "",
    group: "",
    first_name: "",
    last_name: "",
    email: "",
  });

  const [validation, setValidation] = useState({
    username: "",
    password1: "",
    password2: "",
    group: "",
    first_name: "",
    last_name: "",
    email: "",
  });

  const [showPassword, setShowPassword] = useState({
    password1: false,
    password2: false,
  });

  // Handle successful creation
  useEffect(() => {
    if (isUserCreated) {
      // Reset form and close modal
      setFormData({
        username: "",
        password1: "",
        password2: "",
        group: "",
        first_name: "",
        last_name: "",
        email: "",
      });
      setValidation({
        username: "",
        password1: "",
        password2: "",
        group: "",
        first_name: "",
        last_name: "",
        email: "",
      });
      setShowPassword({
        password1: false,
        password2: false,
      });
      toggle();
      dispatch(resetUserFlags());
    }
  }, [isUserCreated, toggle, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error when user starts typing
    if (validation[name]) {
      setValidation((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(`${label} copied to clipboard!`);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Username validation
    if (!formData.username.trim()) {
      errors.username = "Username is required";
    } else if (!/^[a-zA-Z0-9@.+-_]+$/.test(formData.username)) {
      errors.username = "Username may contain only letters, numbers, and @/./+/-/_ characters";
    } else if (users.some(user => user.username === formData.username.trim())) {
      errors.username = "Username already exists";
    }

    // First name validation
    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    } else if (formData.first_name.trim().length < 2) {
      errors.first_name = "First name must be at least 2 characters long";
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.first_name.trim())) {
      errors.first_name = "First name can only contain letters, spaces, hyphens, and apostrophes";
    }

    // Last name validation
    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    } else if (formData.last_name.trim().length < 2) {
      errors.last_name = "Last name must be at least 2 characters long";
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.last_name.trim())) {
      errors.last_name = "Last name can only contain letters, spaces, hyphens, and apostrophes";
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      } else if (users.some(user => user.email === formData.email.trim())) {
        errors.email = "Email already exists";
      }
    }

    // Password validation
    if (!formData.password1.trim()) {
      errors.password1 = "Password is required";
    } else if (formData.password1.length < 8) {
      errors.password1 = "Password must be at least 8 characters long";
    } else if (!/^(?=.*[0-9])(?=.*[A-Za-z]).*$/.test(formData.password1)) {
      errors.password1 = "Password must contain both letters and numbers";
    } else if (/^\d+$/.test(formData.password1)) {
      errors.password1 = "Password must not be entirely numeric";
    } else if (
      formData.username && 
      (formData.password1.includes(formData.username) || 
       (formData.email && formData.password1.includes(formData.email.split("@")[0])))
    ) {
      errors.password1 = "Password is too similar to your username or email";
    }

    // Password confirmation validation
    if (!formData.password2.trim()) {
      errors.password2 = "Please confirm your password";
    } else if (formData.password1 !== formData.password2) {
      errors.password2 = "Passwords do not match";
    }

    // Group validation
    if (!formData.group) {
      errors.group = "Please select a group";
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare data for submission
      const userData = {
        username: formData.username.trim(),
        password1: formData.password1,
        password2: formData.password2,
        group: formData.group,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
      };
      await dispatch(addNewUser(userData)).unwrap();
      // Success handling is done in useEffect
    } catch (error) {
      console.error("User creation failed:", error);
      // Error toast is handled in the thunk
    }
  };

  const handleCancel = () => {
    // Reset form when canceling
    setFormData({
      username: "",
      password1: "",
      password2: "",
      group: "",
      first_name: "",
      last_name: "",
      email: "",
    });
    setValidation({
      username: "",
      password1: "",
      password2: "",
      group: "",
      first_name: "",
      last_name: "",
      email: "",
    });
    setShowPassword({
      password1: false,
      password2: false,
    });
    dispatch(resetUserFlags());
    toggle();
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({
      ...prev,
      password1: result,
      password2: result,
    }));
    
    // Clear password validation if it was invalid
    if (validation.password1 || validation.password2) {
      setValidation((prev) => ({
        ...prev,
        password1: "",
        password2: "",
      }));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="lg" centered>
      <ModalHeader toggle={handleCancel}>
        <i className="ri-user-add-line me-2 text-success"></i>
        Create New User
      </ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label for="username" className="form-label">
                  Username <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  invalid={!!validation.username}
                  placeholder="Enter username"
                  disabled={isLoading}
                />
                {validation.username && <FormFeedback>{validation.username}</FormFeedback>}
                <div className="form-text">
                  May contain letters, numbers, and @/./+/-/_ characters
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="email" className="form-label">
                  Email <span className="text-danger">*</span>
                </Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  invalid={!!validation.email}
                  placeholder="Enter email address"
                  disabled={isLoading}
                />
                {validation.email && <FormFeedback>{validation.email}</FormFeedback>}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="first_name" className="form-label">
                  First Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  invalid={!!validation.first_name}
                  placeholder="Enter first name"
                  disabled={isLoading}
                />
                {validation.first_name && <FormFeedback>{validation.first_name}</FormFeedback>}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="last_name" className="form-label">
                  Last Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  invalid={!!validation.last_name}
                  placeholder="Enter last name"
                  disabled={isLoading}
                />
                {validation.last_name && <FormFeedback>{validation.last_name}</FormFeedback>}
              </div>
            </Col>

            <Col md={8}>
              <div className="mb-3">
                <Label for="password1" className="form-label">
                  Password <span className="text-danger">*</span>
                </Label>
                <InputGroup>
                  <Input
                    type={showPassword.password1 ? "text" : "password"}
                    id="password1"
                    name="password1"
                    value={formData.password1}
                    onChange={handleInputChange}
                    invalid={!!validation.password1}
                    placeholder="Enter password"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={() => togglePasswordVisibility('password1')}
                    disabled={isLoading}
                    title={showPassword.password1 ? "Hide password" : "Show password"}
                  >
                    <i className={`ri-${showPassword.password1 ? 'eye-off' : 'eye'}-line`}></i>
                  </Button>
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={() => copyToClipboard(formData.password1, 'Password')}
                    disabled={isLoading || !formData.password1}
                    title="Copy password to clipboard"
                  >
                    <i className="ri-clipboard-line"></i>
                  </Button>
                </InputGroup>
                {validation.password1 && <FormFeedback className="d-block">{validation.password1}</FormFeedback>}
                <div className="form-text">
                  At least 8 characters with letters and numbers
                </div>
              </div>
            </Col>

            <Col md={4}>
              <div className="mb-3">
                <Label className="form-label">Generate</Label>
                <div>
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={generateRandomPassword}
                    disabled={isLoading}
                    className="w-100"
                    title="Generate secure password"
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Random
                  </Button>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="password2" className="form-label">
                  Confirm Password <span className="text-danger">*</span>
                </Label>
                <InputGroup>
                  <Input
                    type={showPassword.password2 ? "text" : "password"}
                    id="password2"
                    name="password2"
                    value={formData.password2}
                    onChange={handleInputChange}
                    invalid={!!validation.password2}
                    placeholder="Confirm password"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={() => togglePasswordVisibility('password2')}
                    disabled={isLoading}
                    title={showPassword.password2 ? "Hide password" : "Show password"}
                  >
                    <i className={`ri-${showPassword.password2 ? 'eye-off' : 'eye'}-line`}></i>
                  </Button>
                </InputGroup>
                {validation.password2 && <FormFeedback className="d-block">{validation.password2}</FormFeedback>}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="group" className="form-label">
                  Group <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="group"
                  name="group"
                  value={formData.group}
                  onChange={handleInputChange}
                  invalid={!!validation.group}
                  disabled={isLoading}
                >
                  <option value="">Select Group</option>
                  <option value="1">Viewer</option>
                  <option value="2">Editor</option>
                  <option value="3">Admin</option>
                </Input>
                {validation.group && <FormFeedback>{validation.group}</FormFeedback>}
              </div>
            </Col>
          </Row>

          {/* Preview Section */}
          <div className="border-top pt-3 mt-3">
            <h6 className="mb-3">Preview</h6>
            <Row className="g-2">
              <Col md={3}>
                <small className="text-muted">Username:</small>
                <div className="fw-medium">{formData.username || "Not set"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Full Name:</small>
                <div>{`${formData.first_name} ${formData.last_name}`.trim() || "Not set"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Email:</small>
                <div className="text-break">{formData.email || "Not set"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Group:</small>
                <div>
                  <span className="badge bg-soft-secondary text-secondary">
                    {formData.group === "1" ? "Viewer" : 
                     formData.group === "2" ? "Editor" : 
                     formData.group === "3" ? "Admin" : "Not selected"}
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="light"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="success"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              <>
                <i className="ri-user-add-line me-1"></i>
                Create User
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UserCreationModal;